import { Badge } from "@package/subframe/components/Badge";
import { Checkbox } from "@package/subframe/components/Checkbox";
import { Table } from "@package/subframe/components/Table";
import { Link } from "@tanstack/react-router";

import { EmployeeAvatar } from "@/components/people/EmployeeAvatar";
import { SortableHeaderCell } from "@/components/ui/sortable-header-cell";
import {
  TABLE_MIN_WIDTH,
  TableScrollContainer,
} from "@/components/ui/table-scroll-container";
import { AccountStatus } from "@/generated/model/accountStatus";
import type { EmployeeDirectoryEntryDto } from "@/generated/model/employeeDirectoryEntryDto";
import type { SortDir } from "@/lib/category-display";
import {
  accountStatusLabel,
  employmentStatusLabel,
  formatProfileTimestamp,
  NOT_RECORDED,
} from "@/lib/people";
import {
  acknowledgementLabel,
  acknowledgementVariant,
  type DirectorySortColumn,
  employeeName,
  employmentVariant,
} from "@/lib/people-directory";
import { staffUsername } from "@/lib/staff-username";

import { EmployeeRowActions } from "./EmployeeRowActions";

export interface DirectorySelection {
  selectedIds: ReadonlySet<string>;
  allSelected: boolean;
  /** Nobody shown can be selected. */
  noneSelectable: boolean;
  atLimit: boolean;
  onToggle: (userId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
}

interface PeopleDirectoryTableProps {
  employees: EmployeeDirectoryEntryDto[];
  sortColumn: DirectorySortColumn;
  sortDir: SortDir;
  onSort: (column: DirectorySortColumn) => void;
  showLogin: boolean;
  showActions: boolean;
  /** Null when the caller cannot set PINs from the list. */
  selection: DirectorySelection | null;
  onOpen: (userId: string) => void;
  onResetPassword: (employee: EmployeeDirectoryEntryDto) => void;
}

export function PeopleDirectoryTable({
  employees,
  sortColumn,
  sortDir,
  onSort,
  showLogin,
  showActions,
  selection,
  onOpen,
  onResetPassword,
}: PeopleDirectoryTableProps) {
  const sortProps = { activeColumn: sortColumn, activeDir: sortDir, onSort };

  return (
    <TableScrollContainer>
      <Table
        className={TABLE_MIN_WIDTH.xl}
        header={
          <Table.HeaderRow>
            {selection && (
              <Table.HeaderCell className="w-10">
                <Checkbox
                  checked={selection.allSelected}
                  disabled={selection.noneSelectable}
                  onCheckedChange={selection.onToggleAll}
                  aria-label="Select everyone shown"
                />
              </Table.HeaderCell>
            )}
            <SortableHeaderCell column="name" label="Employee" {...sortProps} />
            <SortableHeaderCell column="jobTitle" label="Role" {...sortProps} />
            <SortableHeaderCell
              column="room"
              label="Room / Area"
              {...sortProps}
            />
            <SortableHeaderCell
              column="lineManager"
              label="Line Manager"
              {...sortProps}
            />
            <SortableHeaderCell
              column="employmentStatus"
              label="Employment"
              {...sortProps}
            />
            {showLogin && (
              <SortableHeaderCell
                column="accountStatus"
                label="Account"
                {...sortProps}
              />
            )}
            <SortableHeaderCell
              column="acknowledgements"
              label="Acknowledgements"
              {...sortProps}
            />
            {showLogin && (
              <SortableHeaderCell
                column="lastLogin"
                label="Last Login"
                {...sortProps}
              />
            )}
            {showActions && (
              <Table.HeaderCell variant="aligned-right">
                Actions
              </Table.HeaderCell>
            )}
          </Table.HeaderRow>
        }
      >
        {employees.map((employee) => {
          const name = employeeName(employee);
          const login = employee.loginAndSecurity;
          const selectable = Boolean(selection) && employee.canManageLogin;
          const checked = selection?.selectedIds.has(employee.userId) ?? false;

          return (
            <Table.Row
              key={employee.userId}
              clickable
              className="cursor-pointer"
              data-testid="people-directory-row"
              onClick={() => onOpen(employee.userId)}
            >
              {selection && (
                <Table.Cell
                  className="w-10"
                  onClick={(event) => event.stopPropagation()}
                >
                  {selectable && (
                    <Checkbox
                      checked={checked}
                      disabled={!checked && selection.atLimit}
                      onCheckedChange={(next) =>
                        selection.onToggle(employee.userId, next)
                      }
                      aria-label={`Select ${name}`}
                    />
                  )}
                </Table.Cell>
              )}
              <Table.Cell>
                <div className="flex items-center gap-3">
                  <EmployeeAvatar
                    name={name}
                    photoUrl={employee.photoUrl}
                    size="small"
                    lazy
                  />
                  <div className="flex min-w-0 flex-col items-start">
                    <Link
                      to="/people/$userId"
                      params={{ userId: employee.userId }}
                      className="text-body-bold font-body-bold text-default-font hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {name}
                    </Link>
                    <span className="break-all text-caption font-caption text-subtext-color">
                      {staffUsername(employee.email)}
                    </span>
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell>
                <TextCell value={employee.jobTitle} />
              </Table.Cell>
              <Table.Cell>
                <TextCell value={employee.room?.name} />
              </Table.Cell>
              <Table.Cell>
                <TextCell value={employee.lineManager?.name} />
              </Table.Cell>
              <Table.Cell>
                {employee.employmentStatus ? (
                  <Badge variant={employmentVariant(employee.employmentStatus)}>
                    {employmentStatusLabel(employee.employmentStatus)}
                  </Badge>
                ) : (
                  <TextCell value={null} />
                )}
              </Table.Cell>
              {showLogin && (
                <Table.Cell>
                  {login && (
                    <Badge
                      variant={
                        login.accountStatus === AccountStatus.ACTIVE
                          ? "success"
                          : "neutral"
                      }
                    >
                      {accountStatusLabel(login.accountStatus)}
                    </Badge>
                  )}
                </Table.Cell>
              )}
              <Table.Cell>
                <Badge
                  variant={acknowledgementVariant(
                    employee.acknowledgements.standing,
                  )}
                >
                  {acknowledgementLabel(employee.acknowledgements)}
                </Badge>
              </Table.Cell>
              {showLogin && (
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {formatProfileTimestamp(login?.lastLoginAt)}
                  </span>
                </Table.Cell>
              )}
              {showActions && (
                <Table.Cell
                  variant="aligned-right"
                  onClick={(event) => event.stopPropagation()}
                >
                  <EmployeeRowActions
                    name={name}
                    canResetPassword={employee.canManageLogin}
                    onViewProfile={() => onOpen(employee.userId)}
                    onResetPassword={() => onResetPassword(employee)}
                  />
                </Table.Cell>
              )}
            </Table.Row>
          );
        })}
      </Table>
    </TableScrollContainer>
  );
}

function TextCell({ value }: { value: string | null | undefined }) {
  if (!value) {
    return (
      <span className="text-body font-body text-subtext-color">
        {NOT_RECORDED}
      </span>
    );
  }
  return <span className="text-body font-body text-default-font">{value}</span>;
}
